/**
 * Carrier for a redemption code that the visitor brought in via
 * `account.orboto.io/redeem?code=...` but couldn't redeem yet because
 * they had to sign up + verify email first (OCP-264, Phase B of the
 * unified-redemption epic OCP-262).
 *
 * Stored in `sessionStorage` because:
 *  - it must survive a tab navigation (signup → verify-link click in
 *    the same browser) but should NOT persist across browser sessions
 *    (a stale code in localStorage would auto-fire when the same user
 *    later opens the portal for an unrelated reason);
 *  - it's not a secret in a sensitive sense (codes go through the
 *    server's redemption pipeline which dedups + rate-limits + audits
 *    every attempt), but we still avoid persistent storage so a
 *    shared-machine scenario doesn't accidentally redeem on the next
 *    visitor's account.
 */
const STORAGE_KEY = 'orboto-redeem-pending-code';

export function setPendingRedeemCode(code: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* private-browsing / quota — non-fatal, the URL bar still has the code */
  }
}

export function getPendingRedeemCode(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearPendingRedeemCode(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}

/**
 * OCP-515 - extract the redeem code from a same-site `/redeem?code=X` redirect
 * target (the `?from=` the chooser threads into /signup + /login). Returns null
 * when `target` is not a `/redeem` path or carries no `code`. Trims + caps at
 * 128 chars to mirror the redeem endpoint's own shape gate; the server
 * re-applies the format gate on the signup body, so this is only a client-side
 * best-effort derive, never the source of truth.
 */
export function extractRedeemCodeFromTarget(
  target: string | null | undefined,
): string | null {
  if (!target || typeof target !== 'string') return null;
  // Only same-site relative paths (open-redirect targets are already blocked
  // upstream by safeRedirectTarget, but stay defensive here too).
  if (!target.startsWith('/redeem')) return null;
  const qIndex = target.indexOf('?');
  if (qIndex === -1) return null;
  const params = new URLSearchParams(target.slice(qIndex + 1));
  const code = params.get('code')?.trim();
  if (!code || code.length < 1 || code.length > 128) return null;
  return code;
}
